import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Chip, CircularProgress, TextField, Stack, Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import GestureIcon from '@mui/icons-material/Gesture';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { api, imgUrl } from '../../api/api';
import toast from 'react-hot-toast';

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ContractPdfSettings() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const [archive, setArchive] = useState([]);
  const [hasActive, setHasActive] = useState(false);

  // Provider 서명
  const [provSig, setProvSig] = useState(null);
  const [provSigUploading, setProvSigUploading] = useState(false);

  const baseOrigin = imgUrl || window.location.origin;

  const loadProvSig = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/provider-signature');
      setProvSig(data);
    } catch (_) { setProvSig(null); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/contract-master');
      setActive(data.active);
      setArchive(Array.isArray(data.archive) ? data.archive : []);
      setHasActive(!!data.hasActive);
    } catch (e) {
      toast.error('목록을 불러오지 못했습니다: ' + (e.response?.data?.message || e.message));
      setArchive([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadProvSig(); }, [load, loadProvSig]);

  const openPdf = (urlPath) => {
    window.open(`${baseOrigin}${urlPath}`, '_blank', 'noopener,noreferrer');
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('PDF만 업로드할 수 있습니다.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      await api.post('/admin/contract-master', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('업로드되었으며 활성 마스터로 설정되었습니다.');
      await load();
    } catch (err) {
      toast.error('업로드 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const setActiveFile = async (fileName) => {
    try {
      await api.patch('/admin/contract-master/active', { fileName });
      toast.success('활성 마스터가 변경되었습니다.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const removeFile = async (fileName) => {
    if (!window.confirm('이 버전을 삭제할까요? (활성 파일은 삭제할 수 없습니다)')) return;
    try {
      await api.delete(`/admin/contract-master/${encodeURIComponent(fileName)}`);
      toast.success('삭제되었습니다.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const onProvSigUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProvSigUploading(true);
    try {
      const fd = new FormData();
      fd.append('signature', file);
      await api.post('/admin/provider-signature', fd);
      toast.success('Provider 서명이 저장되었습니다.');
      await loadProvSig();
    } catch (err) {
      toast.error('업로드 실패: ' + (err.response?.data?.message || err.message));
    } finally { setProvSigUploading(false); }
  };

  const deleteProvSig = async () => {
    if (!window.confirm('Provider 서명을 삭제할까요?')) return;
    try {
      await api.delete('/admin/provider-signature');
      toast.success('삭제되었습니다.');
      await loadProvSig();
    } catch (err) { toast.error(err.response?.data?.message || err.message); }
  };

  // 서명 링크 생성
  const [signLinkName, setSignLinkName] = useState('Minjung Lee');
  const [signLink, setSignLink] = useState('');
  const [signLinkLoading, setSignLinkLoading] = useState(false);

  const generateSignLink = async () => {
    setSignLinkLoading(true);
    try {
      const { data } = await api.post('/admin/provider-sign-link', { signerName: signLinkName });
      setSignLink(data.signUrl);
      toast.success('서명 링크가 생성되었습니다');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
    setSignLinkLoading(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(signLink);
    toast.success('링크가 복사되었습니다');
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {/* ── Provider 서명 ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <GestureIcon sx={{ color: '#551FFF', fontSize: 22 }} />
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>
          Provider 서명 (계약서 자동 삽입)
        </Typography>
      </Box>
      <Alert severity="info" sx={{ mb: 2, fontSize: { xs: 11, sm: 12 } }}>
        등록된 서명 이미지가 있으면, 계약서 생성/발송 시 <strong>Provider 서명란에 자동으로 삽입</strong>됩니다.
        PNG 파일을 직접 업로드하거나, <strong>서명 링크</strong>를 생성하여 서명자에게 보낼 수 있습니다.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        {provSig?.hasSignature ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <img src={`${baseOrigin}${provSig.url}?t=${Date.now()}`} alt="Provider signature"
              style={{ maxHeight: 60, border: '1px solid #e5e7eb', borderRadius: 4, padding: 4 }} />
            <Button size="small" variant="outlined" component="label" disabled={provSigUploading}
              startIcon={provSigUploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
              sx={{ borderColor: '#551FFF', color: '#551FFF', textTransform: 'none' }}>
              교체
              <input type="file" hidden accept=".png,image/png" onChange={onProvSigUpload} />
            </Button>
            <Button size="small" color="error" onClick={deleteProvSig} startIcon={<DeleteOutlineIcon />}>
              삭제
            </Button>
          </Box>
        ) : (
          <Button variant="contained" component="label" disabled={provSigUploading}
            startIcon={provSigUploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' }, textTransform: 'none' }}>
            서명 PNG 업로드
            <input type="file" hidden accept=".png,image/png" onChange={onProvSigUpload} />
          </Button>
        )}
      </Paper>

      {/* 서명 링크 생성 */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, background: '#fffbeb' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400e', mb: 1 }}>
          <LinkIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          서명 링크 생성
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#78716c', mb: 1.5 }}>
          링크를 받은 사람이 브라우저에서 직접 서명할 수 있습니다. (유효기간 7일)
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField size="small" label="서명자 이름" value={signLinkName}
            onChange={(e) => setSignLinkName(e.target.value)}
            sx={{ minWidth: 180, '& input': { fontSize: 13 } }} />
          <Button variant="contained" onClick={generateSignLink} disabled={signLinkLoading || !signLinkName.trim()}
            startIcon={signLinkLoading ? <CircularProgress size={14} /> : <LinkIcon />}
            sx={{ background: '#d97706', '&:hover': { background: '#b45309' }, textTransform: 'none', whiteSpace: 'nowrap' }}>
            {signLinkLoading ? '생성 중...' : '링크 생성'}
          </Button>
        </Stack>
        {signLink && (
          <Box sx={{ mt: 1.5, p: 1.5, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#551FFF', wordBreak: 'break-all', flex: 1 }}>
              {signLink}
            </Typography>
            <Tooltip title="복사">
              <Button size="small" onClick={copyLink} sx={{ minWidth: 0, p: 0.5 }}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
            <Button size="small" variant="outlined" onClick={() => window.open(signLink, '_blank')}
              sx={{ fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap' }}>
              열기
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── 마스터 PDF ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <PictureAsPdfIcon sx={{ color: '#551FFF', fontSize: 22 }} />
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>
          계약서 PDF (마스터)
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          새로고침
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2, fontSize: { xs: 11, sm: 12 }, '& .MuiAlert-message': { lineHeight: 1.6 } }}>
        <strong>마스터 PDF</strong>가 있으면 &quot;계약서 생성&quot;·&quot;발송&quot; 시 <strong>시스템이 만드는 PDF 대신</strong> 이 파일을
        매장별 이름으로 복사해 사용합니다. 내용 수정은 Acrobat 등에서 편집한 뒤 <strong>새 PDF를 다시 업로드</strong>하면 됩니다.
        마스터가 없으면 기존처럼 CMS가 PDF를 자동 생성합니다.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155', mb: 1 }}>새 PDF 올리기 (교체)</Typography>
        <Button
          variant="contained"
          component="label"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          disabled={uploading}
          sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' }, textTransform: 'none' }}
        >
          PDF 선택 후 업로드
          <input type="file" hidden accept=".pdf,application/pdf" onChange={onUpload} />
        </Button>
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1 }}>
          업로드 즉시 <strong>활성 마스터</strong>로 설정됩니다. 이전 파일은 목록에 남습니다.
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2, background: '#f8fafc' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155', mb: 0.5 }}>현재 활성 마스터</Typography>
            {hasActive && active?.activeFileName ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Chip icon={<StarIcon />} label={active.originalName || active.activeFileName} size="small" color="primary" variant="outlined" />
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {active.activeFileName} · {active.updatedAt ? new Date(active.updatedAt).toLocaleString('ko-KR') : ''}
                </Typography>
                <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => openPdf(active.url)}>
                  미리보기
                </Button>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>등록된 마스터 없음 — 자동 생성 PDF 사용 중</Typography>
            )}
          </Paper>

          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>버전 목록</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>파일</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>크기</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>업로드 시각</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {archive.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: '#94a3b8', fontSize: 12, py: 3 }}>
                      아직 업로드된 마스터 PDF가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  archive.map((row) => (
                    <TableRow key={row.fileName} hover selected={row.isActive}>
                      <TableCell sx={{ fontSize: 12 }}>
                        {row.fileName}
                        {row.isActive && <Chip label="활성" size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: 10 }} />}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: '#64748b' }}>{formatBytes(row.size)}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: '#64748b' }}>
                        {new Date(row.mtime).toLocaleString('ko-KR')}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openPdf(row.url)} sx={{ fontSize: 10, minWidth: 0, mr: 0.5 }}>
                          보기
                        </Button>
                        {!row.isActive && (
                          <>
                            <Button size="small" onClick={() => setActiveFile(row.fileName)} sx={{ fontSize: 10, minWidth: 0, mr: 0.5 }}>
                              활성으로
                            </Button>
                            <Button size="small" color="error" onClick={() => removeFile(row.fileName)} sx={{ fontSize: 10, minWidth: 0 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
